import type { CandidateProfile } from '../../config/candidates';
import type { IntelligenceAnalysis, ATSResult } from '../../types/api';
import HelpBubble from '../ui/HelpBubble';

interface CandidateGridProps {
  candidates: Array<{
    profile: CandidateProfile;
    analysis: IntelligenceAnalysis | null;
    atsResult: ATSResult | null;
  }>;
  selectedId: string;
  onSelect: (candidateId: string) => void;
}

const AVATAR_COLORS: Record<CandidateProfile['archetype'], { bg: string; text: string }> = {
  exceptional: { bg: 'bg-ti-green/10', text: 'text-ti-green' },
  'ats-darling': { bg: 'bg-ti-amber/10', text: 'text-ti-amber' },
  'hidden-gem': { bg: 'bg-ti-cyan/10', text: 'text-ti-cyan' },
};

const REC_LABELS: Record<string, string> = {
  exceptional: 'Exceptional Fit',
  'strong-fit': 'Strong Fit',
  'good-fit': 'Good Fit',
  'weak-fit': 'Weak Fit',
  'poor-fit': 'Poor Fit',
};

/** Badge colors tied to recommendation level, not archetype */
function getRecColors(rec: string): { bg: string; text: string } {
  switch (rec) {
    case 'exceptional': return { bg: 'bg-ti-green/10', text: 'text-ti-green' };
    case 'strong-fit': return { bg: 'bg-ti-green/10', text: 'text-ti-green' };
    case 'good-fit': return { bg: 'bg-ti-amber/10', text: 'text-ti-amber' };
    case 'weak-fit': return { bg: 'bg-orange-500/10', text: 'text-orange-400' };
    case 'poor-fit': return { bg: 'bg-ti-red/10', text: 'text-ti-red' };
    default: return { bg: 'bg-neutral-700/30', text: 'text-neutral-400' };
  }
}

export default function CandidateGrid({ candidates, selectedId, onSelect }: CandidateGridProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <p className="text-xs text-ti-text-muted uppercase tracking-widest">
          Select a candidate to view detailed analysis
        </p>
        <HelpBubble
          content="Three candidates. Same role. Dramatically different results. The ATS sees keywords — the Intelligence Engine sees capability. Notice how scores shift when you change the role requirements: the 'best' candidate depends entirely on what you're actually looking for."
          source="&quot;The entire system optimizes for volume processed rather than quality matched.&quot;"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {candidates.map(({ profile, analysis, atsResult }) => {
          const isSelected = profile.id === selectedId;
          const avatarColors = AVATAR_COLORS[profile.archetype];
          const matchScore = analysis?.match_score ?? null;
          const atsScore = atsResult?.overall_score ?? null;

          return (
            <button
              key={profile.id}
              onClick={() => onSelect(profile.id)}
              className={`
                relative text-left rounded-xl border p-5 transition-all duration-200 cursor-pointer
                ${isSelected
                  ? `border-ti-blue bg-ti-blue/5 shadow-[0_0_20px_rgba(59,130,246,0.15)] scale-[1.02]`
                  : 'border-ti-border bg-ti-surface hover:border-ti-text-dim hover:bg-ti-surface/80'
                }
              `}
            >
              {/* Avatar + Name row */}
              <div className="flex items-center gap-3 mb-3">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold
                  ${avatarColors.bg} ${avatarColors.text}
                `}>
                  {profile.avatar}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-ti-text truncate">{profile.name}</div>
                  <div className="text-xs text-ti-text-muted truncate">{profile.title}</div>
                </div>
              </div>

              {/* Tagline */}
              <p className="text-xs text-ti-text-dim mb-4">{profile.tagline}</p>

              {/* Scores row */}
              <div className="flex items-center gap-4">
                {matchScore !== null && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-ti-text-muted uppercase tracking-wider">Intel Brief</span>
                    <span className={`text-sm font-bold ${matchScore >= 80 ? 'text-ti-green' : matchScore >= 60 ? 'text-ti-amber' : 'text-ti-red'}`}>
                      {matchScore}
                    </span>
                  </div>
                )}
                {atsScore !== null && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-ti-text-muted uppercase tracking-wider">ATS</span>
                    <span className="text-sm font-bold text-neutral-400">{atsScore}</span>
                  </div>
                )}
                {matchScore === null && atsScore === null && (
                  <div className="text-xs text-ti-text-dim italic">Analyzing...</div>
                )}
              </div>

              {/* Recommendation badge from Intelligence Brief */}
              {analysis?.recommendation && (() => {
                const recColors = getRecColors(analysis.recommendation);
                return (
                  <div className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-medium ${recColors.bg} ${recColors.text}`}>
                    {REC_LABELS[analysis.recommendation] ?? analysis.recommendation}
                  </div>
                );
              })()}

              {/* Selected indicator */}
              {isSelected && (
                <div className="absolute -top-px -left-px -right-px -bottom-px rounded-xl border-2 border-ti-blue pointer-events-none" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
